import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { Tag } from "antd";
import {
    useGetConnectionSequenceStepLogsExportMutation,
    useGetConnectionSequenceStepLogsQuery,
} from "api/TenantSlug/ConnectionSequenceStepLogs/hooks.ts";
import { LogConnectionDrawerDetail } from "components/Drawers/Logs/LogConnectionDrawerDetail.tsx";
import { ShortenedIdWithCopy } from "components/UI/ShortenedIdWithCopy";
import { Table } from "components/UI/Table";
import { exportAtom } from "components/UI/Table/atoms/exportAtom";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, tableAtom } from "components/UI/Table/atoms/tableAtom";
import { FILTER_TYPES } from "components/UI/Table/constants/filter";
import { useTableDataApiOperations } from "components/UI/Table/hooks/useTableDataApiOperations";
import { TableColumnType } from "components/UI/Table/types/table";
import { ApiResponseBodyCell } from "components/UI/Table/UI/ApiResponseBodyCell/ApiResponseBodyCell.tsx";
import { ColumnHeaderTitle } from "components/UI/Table/UI/ColumnHeaderTitle";
import { DateTimeCell } from "components/UI/Table/UI/DateTimeCell/DateTimeCell.tsx";
import { EntityLinkCell } from "components/UI/Table/UI/EntityLinkCell/EntityLinkCell.tsx";
import { useCreatedAtFilterValue } from "components/UI/Table/utils/getDefaultCreatedAtFilter";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions";
import { useSmartRowHandlers } from "hooks/useTableRowHandlers.tsx";
import { Key } from "react";
import { useRecoilValue } from "recoil";
import { ConnectionSequenceStepLogModelType, PermissionEnum } from "types/enums";
import { DrawerId, TableId } from "types/models";

import ConnectionSequenceStepLogData = App.Data.ConnectionSequenceStepLogData;
import GetConnectionSequenceStepLogsResponse = App.Http.Responses.ConnectionSequenceStepLog.GetConnectionSequenceStepLogsResponse;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;

export const IntegrationStepLogsTable = () => {
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const { t: tGL } = usePaldockTranslation("tables.general-labels");
    const tableId = TableId.IntegrationStepLogsTable;
    const tableAtomInstance = tableAtom<ConnectionSequenceStepLogData>();
    const tableState = useRecoilValue(tableAtomInstance(tableId));
    const createdAtStr = useCreatedAtFilterValue();
    const exportState = useRecoilValue(exportAtom(tableId));

    const request = {
        page: {
            number: tableState.page?.number ?? DEFAULT_PAGE_NUMBER,
            size: tableState.page?.size ?? DEFAULT_PAGE_SIZE,
        },
        filter: {
            filter: {
                model_type: ConnectionSequenceStepLogModelType.Integration,
                ...(tableState.filter?.filter || {}),
                ...(tableState.filter?.filter?.created_at
                    ? {}
                    : {
                          created_at: {
                              btw: createdAtStr,
                          },
                      }),
            },
            sort: tableState?.filter?.sort ?? "-created_at",
            include: "connectionSequenceStep,offer,conversionState,owner,advertiser,cascade,integration",
        },
    };

    const { getRowHandlers, getRowClassName } = useSmartRowHandlers<ConnectionSequenceStepLogData>(
        {
            entityName: "connectionSequenceStepLog",
            detailPermission: PermissionEnum.LogShowDetail,
            detailDrawerId: DrawerId.ConnectionSequenceStepLog,
            detailDrawerParam: DrawerId.ConnectionSequenceStepLog,
        },
        {
            excludeColumns: ["response"],
        }
    );

    const getConnectionSequenceStepLogsQuery = useGetConnectionSequenceStepLogsQuery(request, { enabled: false });

    const getConnectionSequenceStepLogsExportMutation = useGetConnectionSequenceStepLogsExportMutation();

    const tableFetchDataUtils = useTableDataApiOperations<
        GetConnectionSequenceStepLogsResponse,
        void,
        Record<string, never>,
        BaseExportRequest
    >(tableId, getConnectionSequenceStepLogsQuery, undefined, undefined, getConnectionSequenceStepLogsExportMutation);
    const permissionsUtils = usePermission();

    const columnConfig: TableColumnType<ConnectionSequenceStepLogData>[] = [
        {
            title: <ColumnHeaderTitle columnTitle={tG("date")} />,
            dataIndex: "created_at",
            key: "created_at",
            inputType: "text",
            render: (v) => <DateTimeCell value={v} showSeconds={true} />,
            sorter: true,
            defaultSorter: true,
            defaultSortOrder: "descend",
            width: 170,
            ellipsis: true,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("conversion_id")} />,
            dataIndex: "conversionState.origin_eid",
            key: "conversionState.origin_eid",
            inputType: "text",
            render: (_, data) =>
                data.conversionState?.origin_eid ? <ShortenedIdWithCopy id={data.conversionState.origin_eid} /> : "-",
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "conversionState.origin_eid",
            sorter: true,
            width: 150,
            ellipsis: true,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("event_id")} />,
            dataIndex: "conversionState.eid",
            key: "conversionState.eid",
            inputType: "text",
            render: (_, data) =>
                data.conversionState?.eid ? <ShortenedIdWithCopy id={data.conversionState.eid} /> : "-",
            filterable: true,
            filterType: FILTER_TYPES.eid,
            sorter: true,
            width: 150,
            ellipsis: true,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("offer")} />,
            dataIndex: "offer.title",
            key: "offer.title",
            inputType: "text",
            render: (_, result) => {
                const offer = result.offer;
                return <EntityLinkCell.Offer entity={offer} />;
            },
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "offer.title",
            sorter: true,
            width: 150,
            ellipsis: true,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("channel")} />,
            dataIndex: "cascade.title",
            key: "cascade.title",
            inputType: "text",
            render: (_, result) => {
                const cascade = result.cascade;
                return <EntityLinkCell.Cascade entity={cascade} />;
            },
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "cascade.title",
            sorter: true,
            width: 150,
            ellipsis: true,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("integration")} />,
            dataIndex: "integration.name",
            key: "integration.name",
            inputType: "text",
            render: (_, result) => {
                const integration = result.integration;
                return <EntityLinkCell.Integration entity={integration} />;
            },
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "integration.name",
            sorter: true,
            width: 150,
            ellipsis: true,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("affiliate")} />,
            key: "owner.nickname",
            dataIndex: "owner.nickname",
            inputType: "text",
            render: (_, result) => {
                const owner = result.owner;
                return <EntityLinkCell.Owner entity={owner} />;
            },
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "owner.nickname",
            sorter: true,
            width: 150,
            ellipsis: true,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("method_name")} />,
            dataIndex: "request_name",
            key: "request_name",
            inputType: "text",
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "request_name",
            sorter: true,
            width: 150,
            ellipsis: true,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("status")} />,
            dataIndex: "http_status",
            key: "http_status",
            inputType: "text",
            filterable: true,
            filterType: FILTER_TYPES.number,
            filterName: "http_status",
            sorter: true,
            width: 100,
            ellipsis: true,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("state")} />,
            dataIndex: "is_success",
            key: "status",
            render: (is_success) => {
                return is_success ? (
                    <Tag icon={<CheckCircleOutlined />} color="green">
                        {tGL("ok")}
                    </Tag>
                ) : (
                    <Tag icon={<CloseCircleOutlined />} color="red">
                        {tGL("fail")}
                    </Tag>
                );
            },
            filterable: true,
            filterType: FILTER_TYPES.select,
            filterName: "is_success",
            filterOptions: [
                { label: tGL("ok"), value: true },
                { label: tGL("fail"), value: false },
            ],
            sorter: true,
            width: 100,
            ellipsis: true,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("request")} />,
            dataIndex: "request",
            key: "request",
            inputType: "text",
            render: (v, row) => {
                const id = `request-${row.id}`;
                const parsedValue = JSON.parse(v);

                return <ApiResponseBodyCell response={parsedValue} id={id} />;
            },
            // filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "request",
            width: 390,
            ellipsis: true,
            visible: false,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("response")} />,
            dataIndex: "response",
            key: "response",
            inputType: "text",
            render: (v, row) => {
                const id = `response-${row.id}`;
                const parsedValue = JSON.parse(v);

                return <ApiResponseBodyCell response={parsedValue} id={id} />;
            },
            // filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "response",
            width: 390,
            ellipsis: true,
        },
    ];

    return (
        <>
            <Table<GetConnectionSequenceStepLogsResponse, ConnectionSequenceStepLogData>
                tableId={tableId}
                data={tableFetchDataUtils.tableData as GetConnectionSequenceStepLogsResponse}
                dataSource={tableFetchDataUtils.tableData?.connectionSequenceStepLogs || []}
                columnConfig={columnConfig}
                loading={getConnectionSequenceStepLogsQuery.isLoading || tableFetchDataUtils.fetchQuery.isFetching}
                onRefresh={tableFetchDataUtils.refetchData}
                showRefetchButton={true}
                rowKey={(record) => record.id as Key}
                enableFilters={permissionsUtils.hasPermission(PermissionEnum.LogSelectFilter)}
                enableColumnsVisibility={permissionsUtils.hasPermission(PermissionEnum.LogColumnSetting)}
                onRow={(record, rowIndex) => getRowHandlers(record, rowIndex)}
                rowClassName={(_, rowIndex) => getRowClassName(rowIndex)}
                enableTimeFilter={permissionsUtils.hasPermission(PermissionEnum.LogSelectFilterTime)}
                hideActionsColumn={true}
                dateFilterDataIndex={"created_at"}
                autoScrollWidth={true}
                onExport={() =>
                    tableFetchDataUtils.exportData({
                        extension: exportState.extension,
                        headers: exportState.headers,
                        export_all: exportState.range === "all",
                        page: request.page,
                        filter: request.filter,
                        delimiter: exportState.delimiter,
                    })
                }
            />
            <LogConnectionDrawerDetail type={ConnectionSequenceStepLogModelType.Integration} />
        </>
    );
};
